import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  Plus,
  Search,
  Calendar,
  Timer,
  Eye,
  Users as UsersIcon,
  FileSearch,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { hasRole } from "../utils/auth";
import { loadKasus, STATUS_KESELURUHAN } from "../utils/auditStorage";
import { loadHariLibur } from "../utils/hariLiburStorage";
import { sisaHariKerja, formatTanggalID, todayIso } from "../utils/hariKerja";

function sisaBadge(sisa, statusKeseluruhan) {
  if (statusKeseluruhan === STATUS_KESELURUHAN.SELESAI)
    return { label: "Selesai", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
  if (statusKeseluruhan === STATUS_KESELURUHAN.MELEWATI_BATAS)
    return { label: `Lewat ${Math.abs(sisa)} HK`, cls: "bg-slate-900 text-white border-slate-900" };
  if (sisa < 0) return { label: `Lewat ${Math.abs(sisa)} HK`, cls: "bg-slate-900 text-white border-slate-900" };
  if (sisa < 7) return { label: `${sisa} HK tersisa`, cls: "bg-red-100 text-red-800 border-red-200" };
  if (sisa <= 15)
    return { label: `${sisa} HK tersisa`, cls: "bg-amber-100 text-amber-800 border-amber-200" };
  return { label: `${sisa} HK tersisa`, cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
}

export default function AuditInvestigasi() {
  const { user } = useAuth();
  const [kasus, setKasus] = useState([]);
  const [loadingKasus, setLoadingKasus] = useState(true);
  const [hariLibur] = useState(() => loadHariLibur());
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("ALL");
  const [tahun, setTahun] = useState("ALL");

  const canEdit = hasRole(user, "IRBAN", "DALNIS");

  useEffect(() => {
    let mounted = true;
    loadKasus()
      .then((data) => {
        if (mounted) {
          setKasus(data);
          setLoadingKasus(false);
        }
      })
      .catch(() => {
        if (mounted) setLoadingKasus(false);
      });
    return () => { mounted = false; };
  }, []);

  const enriched = useMemo(() => {
    const t = todayIso();
    return kasus.map((k) => ({
      ...k,
      sisaHK: k.deadlineTL60HK ? sisaHariKerja(t, k.deadlineTL60HK, hariLibur) : 0,
    }));
  }, [kasus, hariLibur]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enriched.filter((k) => {
      if (status !== "ALL" && k.statusKeseluruhan !== status) return false;
      if (tahun !== "ALL" && (k.tanggalLHP || "").slice(0, 4) !== tahun) return false;
      if (q) {
        const hay = `${k.nomorLHP} ${k.judulKasus}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [enriched, search, status, tahun]);

  const tahunOptions = useMemo(() => {
    const set = new Set(enriched.map((k) => (k.tanggalLHP || "").slice(0, 4)).filter(Boolean));
    return [...set].sort().reverse();
  }, [enriched]);

  return (
    <div className="space-y-5" data-testid="audit-list-page">
      <div className="flex flex-col gap-3 border-b border-gray-200 pb-5 md:flex-row md:items-end md:justify-between">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
            Modul Kritikal
          </div>
          <h1 className="mt-1 font-display text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
            TL Riksus & AI
          </h1>
          <p className="mt-1 text-[13px] text-slate-500">
            Daftar kasus audit investigasi dengan perhitungan deadline 60 hari kerja dari tanggal penyerahan.
          </p>
        </div>
        {canEdit ? (
          <Link
            to="/audit-investigasi/baru"
            data-testid="audit-new-btn"
            className="inline-flex h-10 items-center gap-1.5 rounded-md bg-[#1e3a5f] px-4 text-[13px] font-semibold text-white hover:bg-[#152a46]"
          >
            <Plus className="h-4 w-4" /> Kasus Baru
          </Link>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-white p-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari nomor LHP atau judul…"
            data-testid="audit-search"
            className="h-9 w-full rounded-md border border-gray-300 bg-white pl-8 pr-3 text-[13px]"
          />
        </div>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          data-testid="audit-filter-status"
          className="h-9 rounded-md border border-gray-300 bg-white px-2.5 text-[13px]"
        >
          <option value="ALL">Semua Status</option>
          <option value="AKTIF">Aktif</option>
          <option value="SELESAI">Selesai</option>
          <option value="MELEWATI_BATAS">Melewati Batas</option>
        </select>
        <select
          value={tahun}
          onChange={(e) => setTahun(e.target.value)}
          data-testid="audit-filter-tahun"
          className="h-9 rounded-md border border-gray-300 bg-white px-2.5 text-[13px]"
        >
          <option value="ALL">Semua Tahun</option>
          {tahunOptions.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      </div>

      {loadingKasus ? (
        <div className="flex items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
          <div className="text-[12px] text-slate-500">Memuat kasus…</div>
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 bg-white px-6 py-16 text-center">
          <FileSearch className="h-6 w-6 text-slate-400" />
          <h3 className="mt-3 font-display text-base font-semibold text-slate-900">
            Belum ada kasus Audit Investigasi
          </h3>
          <p className="mt-1 text-[12px] text-slate-500">
            {canEdit
              ? "Klik 'Kasus Baru' untuk menambahkan kasus pertama."
              : "Kasus akan muncul setelah Irban atau Dalnis menambahkan."}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-[12.5px]" data-testid="audit-table">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-[0.1em] text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">No LHP</th>
                <th className="px-3 py-2 text-left font-semibold">Tanggal LHP</th>
                <th className="px-3 py-2 text-left font-semibold">Judul</th>
                <th className="px-3 py-2 text-left font-semibold">Deadline 60HK</th>
                <th className="px-3 py-2 text-left font-semibold">Sisa HK</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-right font-semibold">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {visible.map((k) => {
                const b = sisaBadge(k.sisaHK, k.statusKeseluruhan);
                return (
                  <tr key={k.id} data-testid={`audit-row-${k.id}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-[11.5px] text-slate-800">{k.nomorLHP}</td>
                    <td className="px-3 py-2">{formatTanggalID(k.tanggalLHP)}</td>
                    <td className="max-w-[320px] px-3 py-2">
                      <div className="truncate font-medium text-slate-900" title={k.judulKasus}>
                        {k.judulKasus}
                      </div>
                      <div className="mt-0.5 inline-flex items-center gap-1 text-[10.5px] text-slate-500">
                        <UsersIcon className="h-3 w-3" />
                        {k.terperiksa?.length || 0} terperiksa · {k.rekomendasi?.length || 0} rekomendasi
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2">
                      <div className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-slate-400" />
                        {k.deadlineTL60HK ? formatTanggalID(k.deadlineTL60HK) : <span className="text-red-500 italic">Belum Set</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${b.cls}`}
                        data-testid={`audit-sisa-${k.id}`}
                      >
                        <Timer className="h-3 w-3" />
                        {b.label}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
                          k.statusKeseluruhan === "SELESAI"
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : k.statusKeseluruhan === "MELEWATI_BATAS"
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-blue-200 bg-blue-50 text-blue-700"
                        }`}
                      >
                        {k.statusKeseluruhan}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Link
                        to={`/audit-investigasi/${k.id}`}
                        data-testid={`audit-view-${k.id}`}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-[11px] font-semibold text-slate-700 hover:border-[#1e3a5f] hover:text-[#1e3a5f]"
                      >
                        <Eye className="h-3 w-3" /> Detail
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
